import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Grid,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { fileService, groupService, onlyofficeService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import SharePermissionsDialog from "../components/SharePermissionsDialog";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

const FileDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [officeEditable, setOfficeEditable] = useState(false);
  const [officeViewable, setOfficeViewable] = useState(false);

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Obtém o ID da pasta anterior da URL ou do estado da localização
  const previousFolderId = location.state?.folderId || null;

  const { getRootProps, getInputProps, acceptedFiles, open } = useDropzone({
    maxFiles: 1,
    noClick: false,
    noKeyboard: false,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "text/plain": [".txt"],
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
      "application/vnd.ms-excel.sheet.binary.macroEnabled.12": [".xlsb"],
      "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [".xltx"],
      "application/vnd.ms-excel.template": [".xlt"],
    },
  });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // Fetch file details and versions
        const fileResponse = await fileService.getFile(id);
        if (cancelled) return;
        setFile(fileResponse.data);

        // Fetch file versions (admin only)
        if (currentUser?.role === "ADMIN") {
          const versionsResponse = await fileService.getFileVersions(id);
          if (cancelled) return;
          setVersions(versionsResponse.data);
        }

        // Check if file is editable in OnlyOffice
        try {
          const editableResponse = await onlyofficeService.checkEditable(id);
          if (cancelled) return;
          setOfficeEditable(editableResponse.data.editable);
          setOfficeViewable(editableResponse.data.viewable);

          // Preload OnlyOffice script so it's cached when user clicks Edit
          if (editableResponse.data.editable || editableResponse.data.viewable) {
            const onlyofficeUrl = process.env.REACT_APP_ONLYOFFICE_URL || "";
            if (!onlyofficeUrl) return;
            const link = document.createElement("link");
            link.rel = "preload";
            link.as = "script";
            link.href = `${onlyofficeUrl}/web-apps/apps/api/documents/api.js`;
            document.head.appendChild(link);
          }
        } catch (e) {
          // OnlyOffice check is non-critical
        }
      } catch (err) {
        if (cancelled) return;
        setError("Falha ao carregar detalhes do arquivo");
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id, currentUser?.role]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setUploadProgress(0);
    setUploadError("");
  };

  const handleShareDialogOpen = () => {
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDownload = (url) => {
    window.open(url, "_blank");
  };

  const handleUploadNewVersion = async () => {
    if (acceptedFiles.length === 0) {
      setUploadError("Por favor, selecione um arquivo");
      return;
    }

    const file = acceptedFiles[0];

    try {
      setUploadError("");
      setUploadProgress(10);

      // Criar FormData para enviar o arquivo
      const formData = new FormData();
      formData.append("file", file);
      formData.append("size", file.size);
      formData.append("contentType", file.type);

      console.log("Enviando arquivo para nova versão:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      setUploadProgress(30);

      // Upload da nova versão diretamente para o backend
      const response = await fileService.uploadFileVersion(id, formData);
      const version = response?.data?.version;

      setUploadProgress(100);

      // Update versions list
      if (version) {
        setVersions((prevVersions) => [version, ...prevVersions]);
      }

      // Update file size
      setFile((prevFile) => ({
        ...prevFile,
        size: file.size,
      }));

      handleUploadDialogClose();
    } catch (err) {
      console.error("Erro ao enviar nova versão:", err);
      setUploadError(err.message || "Falha ao enviar nova versão");
    }
  };

  const handleDelete = async () => {
    try {
      await fileService.deleteFile(id);
      navigate("/");
    } catch (err) {
      console.error(err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isAdmin = currentUser && currentUser.role === "ADMIN";
  const isOwner = file && currentUser && file.ownerId === currentUser.id;
  const canEdit =
    isOwner ||
    (file &&
      file.permissions &&
      file.permissions.some(
        (p) => p.access === "WRITE" || p.access === "ADMIN"
      ));

  const handleGoBack = () => {
    if (previousFolderId) {
      // Se tiver ID da pasta anterior, volta para essa pasta
      navigate("/", { state: { currentFolderId: previousFolderId } });
    } else {
      // Caso contrário, volta para a raiz
      navigate("/");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!file) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Arquivo não encontrado
      </Alert>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleGoBack}
        sx={{ mb: 3 }}
      >
        Voltar para Pasta
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enviado por {file.owner.name} em {formatDate(file.createdAt)}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={file.isPublic ? "Público" : "Privado"}
                color={file.isPublic ? "success" : "default"}
                size="small"
                sx={{ mr: 1 }}
              />
              <Chip
                label={formatFileSize(file.size)}
                size="small"
                sx={{ mr: 1 }}
              />
              <Chip label={file.mimeType} size="small" />
            </Box>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(file.downloadUrl)}
              sx={{ mr: 1 }}
            >
              Baixar
            </Button>
            {(officeEditable || officeViewable) && (
              <Button
                variant="outlined"
                startIcon={
                  officeEditable && canEdit ? <EditIcon /> : <VisibilityIcon />
                }
                onClick={() => window.open(`/files/${id}/edit`, "_blank")}
                sx={{ mr: 1 }}
              >
                {officeEditable && canEdit
                  ? "Editar no Navegador"
                  : "Visualizar no Navegador"}
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleUploadDialogOpen}
                sx={{ mr: 1 }}
              >
                Nova Versão
              </Button>
            )}
            {isAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShareDialogOpen}
                  sx={{ mr: 1 }}
                >
                  Compartilhar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteDialogOpen}
                >
                  Excluir
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            {currentUser?.role === "ADMIN" && <Tab label="Versões" />}
            <Tab label="Detalhes" />
          </Tabs>
        </Box>

        {currentUser?.role === "ADMIN" && tabValue === 0 && (
          <List>
            {versions.map((version) => (
              <ListItem key={version.id} divider>
                <ListItemText
                  primary={`Versão ${version.versionNumber}`}
                  secondary={`Enviado por ${
                    version.createdBy.name
                  } em ${formatDate(version.createdAt)} • ${formatFileSize(
                    version.size
                  )}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDownload(version.downloadUrl)}
                  >
                    <DownloadIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {((currentUser?.role === "ADMIN" && tabValue === 1) ||
          (currentUser?.role !== "ADMIN" && tabValue === 0)) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Informações do Arquivo
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Nome:
              </Typography>
              <Typography variant="body2">{file.name}</Typography>

              <Typography variant="body2" color="text.secondary">
                Tamanho:
              </Typography>
              <Typography variant="body2">
                {formatFileSize(file.size)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Tipo:
              </Typography>
              <Typography variant="body2">{file.mimeType}</Typography>

              <Typography variant="body2" color="text.secondary">
                Visibilidade:
              </Typography>
              <Typography variant="body2">
                {file.isPublic ? "Público" : "Privado"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Criado em:
              </Typography>
              <Typography variant="body2">
                {formatDate(file.createdAt)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Última Atualização:
              </Typography>
              <Typography variant="body2">
                {formatDate(file.updatedAt)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Proprietário:
              </Typography>
              <Typography variant="body2">
                {file.owner.name} ({file.owner.email})
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Versões:
              </Typography>
              <Typography variant="body2">{versions.length}</Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar Nova Versão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Selecione um arquivo para enviar como nova versão.
          </DialogContentText>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: "primary.main",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              mb: 2,
              cursor: "pointer",
              bgcolor: "rgba(0, 0, 0, 0.02)",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            {acceptedFiles.length > 0 ? (
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {acceptedFiles[0].name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(acceptedFiles[0].size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Arraste e solte um arquivo aqui
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Formatos aceitos: Excel (.xls, .xlsx, .xlsm, .xlsb), Word (.doc, .docx), PDF, Imagens, Texto (.txt), ZIP
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  ou clique para selecionar
                </Button>
              </Box>
            )}
          </Box>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ mt: 2 }}>
              <CircularProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Enviando... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancelar</Button>
          <Button
            onClick={handleUploadNewVersion}
            variant="contained"
            disabled={acceptedFiles.length === 0 || uploadProgress > 0}
            startIcon={<UploadIcon />}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <SharePermissionsDialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        itemType="file"
        itemId={id}
        itemName={file ? file.name : ""}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Excluir Arquivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este arquivo? Esta ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button onClick={handleDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileDetails;
